import layoutStyles from '@ds/foundations/layout.module.css';
import { type LayoutElementProps, type LayoutProps, resolveLayoutProps } from '@ds/foundations/layoutProps';
import paddingStyles from '@ds/foundations/padding.module.css';
import { type PaddingProps, resolvePaddingProps } from '@ds/foundations/paddingProps';
import { Slot } from '@ds/foundations/Slot';
import { type StackGap, stackGap } from '@ds/foundations/tokens';
import clsx from 'clsx';
import React, { type CSSProperties, forwardRef, type ReactNode } from 'react';

import styles from './Flex.module.css';

export type FlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';
export type FlexAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type FlexJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
export type FlexGrow = 'row' | 'column';

/**
 * What each `grow` value expands to. They are defaults for the layout props, not CSS of their own:
 * a layout prop the caller sets wins, and there is no rule for a consumer className to tie with.
 */
const GROW_PRESETS = {
    row: { flexGrow: 1, width: 0 },
    column: { flexGrow: 1, minHeight: 0 },
} as const satisfies Record<FlexGrow, LayoutProps>;

/** Fills in each preset key the caller left unset (`undefined` counts as unset). */
const withPreset = <P extends LayoutProps>(props: P, preset?: LayoutProps): P => {
    if (!preset) return props;
    const filled: LayoutProps = { ...props };
    (Object.keys(preset) as (keyof LayoutProps)[]).forEach((key) => {
        if (filled[key] == null) (filled as Record<string, unknown>)[key] = preset[key];
    });
    return filled as P;
};

/**
 * A layout primitive that wraps CSS flexbox.
 *
 * `Flex` has no visual appearance of its own — it only affects the layout of its children.
 * Use it to arrange children in a row or column with consistent spacing, alignment, and
 * justification drawn from the design token scale.
 *
 * Keyword props (`direction`, `align`, `justify`, …) map to `data-*` attributes resolved in
 * `Flex.module.css`; `gap` resolves to a `--space-*` token applied via the `--flex-gap` custom
 * property. Do not use raw pixel values — use token keys for `gap` and the `as` prop for semantics.
 *
 * Also accepts the shared **padding props** (`p`, `px`, `py`, `pt`, `pr`, `pb`, `pl` — see
 * `paddingProps.ts`), so a padded row needs no wrapping `Box`, and the shared **layout props**
 * (`position`, offsets, size, `overflow`, and the flex-item props `flexGrow`/`flexShrink`/`flexBasis`
 * for this Flex as a *child*) — see `layoutProps.ts`. Reach for those before `style`.
 *
 * @example
 * // Horizontal row with centred children and a gap
 * <Flex direction="row" align="center" gap={4}>
 *     <Icon />
 *     <Typography variant="body-1">Label</Typography>
 * </Flex>
 *
 * @example
 * // Full-height column that pushes its last child to the bottom
 * <Flex direction="column" justify="between" grow="column">
 *     <NavItems />
 *     <LogoutButton />
 * </Flex>
 *
 * @example
 * // Padded row — padding and gap on one element, no wrapping Box
 * <Flex align="center" gap={2} px={4} py={2}>
 *     <Icon />
 *     <Typography variant="body-1">Label</Typography>
 * </Flex>
 *
 * @example
 * // Semantic nav element — same flex behaviour, correct HTML landmark
 * <Flex as="nav" direction="row" gap={2}>
 *     <NavItem />
 * </Flex>
 *
 * @example
 * // Containing block for an anchored Badge — no style prop
 * <Flex as="span" inline position="relative">
 *     <IconButton icon={<MailIcon />} label="Notifications" />
 *     <Badge anchorOrigin="top-end">3</Badge>
 * </Flex>
 */
export interface FlexProps extends React.HTMLAttributes<HTMLElement>, LayoutElementProps, PaddingProps, LayoutProps {
    /**
     * Sets `flex-direction`.
     * Omitting this prop leaves the browser default (`row`).
     */
    direction?: FlexDirection;
    /**
     * Sets `align-items` — positions children on the **cross axis**.
     *
     * | Value | CSS |
     * |---|---|
     * | `'start'` | `flex-start` |
     * | `'center'` | `center` |
     * | `'end'` | `flex-end` |
     * | `'stretch'` | `stretch` |
     * | `'baseline'` | `baseline` |
     */
    align?: FlexAlign;
    /**
     * Sets `justify-content` — distributes children along the **main axis**.
     *
     * | Value | CSS |
     * |---|---|
     * | `'start'` | `flex-start` |
     * | `'center'` | `center` |
     * | `'end'` | `flex-end` |
     * | `'between'` | `space-between` |
     * | `'around'` | `space-around` |
     * | `'evenly'` | `space-evenly` |
     */
    justify?: FlexJustify;
    /**
     * Gap between children using `--space-*` token keys.
     *
     * Pass the numeric suffix of the token name — not a raw pixel value.
     * `gap={4}` → `var(--space-4)` → 16px. Only steps 12, 16 and 20 shrink below 1200px.
     *
     * In a row this is the design system's **inline** family (`column-gap`, steps 0–12); in a
     * `direction="column"` Flex it is the **stack** family (`row-gap`, steps 0–20). Not enforced.
     *
     * `gap="section"` is the page-section rhythm for a column that stacks page sections —
     * `--stack-section`, 48px on desktop and 40px below 1200px, which is not a step of the scale.
     *
     * Omitting it means **no gap**, including inside a `Flex` that has one — the value is not
     * inherited, so a nested `Flex` never picks up its parent's spacing by accident.
     *
     * If the value you need is not in the scale, request a token addition
     * rather than reaching for `style={{ gap: '...' }}`.
     *
     * @default 0
     */
    gap?: StackGap;
    /**
     * Enables `flex-wrap: wrap`.
     * When `true`, children flow onto additional lines rather than overflowing.
     */
    wrap?: boolean;
    /**
     * Fills the remaining space in the parent flex container. Pick the value matching the
     * **parent's** direction:
     *
     * - `'row'` — `flexGrow={1}` + `width={0}`. The zero width lets long text wrap or truncate
     *   instead of pushing past the row. Outside a flex row it collapses the element to zero width.
     * - `'column'` — `flexGrow={1}` + `minHeight={0}`. The zero minimum lets the content scroll or
     *   clip instead of overflowing the column.
     *
     * A **preset of layout props**: any of `flexGrow`, `width` or `minHeight` set alongside it
     * overrides that part of the preset, so `grow="row" width="50%"` grows from half the row.
     */
    grow?: FlexGrow;
    /**
     * Uses `inline-flex` instead of `flex`.
     * The element participates in inline layout while still arranging its children as flex items.
     */
    inline?: boolean;
    children?: ReactNode;
}

const Flex = forwardRef<HTMLElement, FlexProps>(
    (
        {
            as: Tag = 'div',
            asChild,
            direction,
            align,
            justify,
            gap,
            wrap,
            grow,
            inline,
            className,
            style,
            children,
            ...others
        },
        ref
    ) => {
        // Padding props split off first, then the layout props (position / offsets / size /
        // overflow / flex-item). Both become gated data-* attributes + custom properties, read by the
        // shared padding.module.css and layout.module.css.
        // `grow` fills in layout props the caller left unset before they resolve.
        const padding = resolvePaddingProps(others);
        const { attrs, vars, rest } = resolveLayoutProps(withPreset(padding.rest, grow && GROW_PRESETS[grow]));

        // asChild renders the one child element instead, with these props merged onto it (Slot).
        const Comp: React.ElementType = asChild ? Slot : Tag;

        return (
            <Comp
                ref={ref}
                data-direction={direction}
                data-align={align}
                data-justify={justify}
                data-wrap={wrap || undefined}
                data-inline={inline || undefined}
                // The gap rule is gated on `data-gap`, like the layout props: a Flex without `gap`
                // declares no gap, so it neither inherits an ancestor's `--flex-gap` nor overrides a
                // consumer className that sets one.
                data-gap={gap != null || undefined}
                {...padding.attrs}
                {...attrs}
                className={clsx(styles.flex, paddingStyles.padding, layoutStyles.layout, className)}
                style={{ '--flex-gap': stackGap(gap), ...padding.vars, ...vars, ...style } as CSSProperties}
                {...rest}
            >
                {children}
            </Comp>
        );
    }
);

Flex.displayName = 'Flex';

export { Flex };
export default Flex;
